import React, { useState, useEffect } from "react";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import type { AnalyticsFilters } from "../../types/analytics";
import { fetchAnalyticsFollowups } from "./analyticsApi";
import type { AnalyticsFollowUpItem } from "../../types/analytics";

interface FollowupsTabProps {
  filters: AnalyticsFilters;
}

function formatDate(d: string | Date | null): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString();
}

const FollowupsTab: React.FC<FollowupsTabProps> = ({ filters }) => {
  const { t } = useLanguage();
  const [today, setToday] = useState<AnalyticsFollowUpItem[]>([]);
  const [tomorrow, setTomorrow] = useState<AnalyticsFollowUpItem[]>([]);
  const [overdue, setOverdue] = useState<AnalyticsFollowUpItem[]>([]);
  const [counts, setCounts] = useState({ today: 0, tomorrow: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAnalyticsFollowups(filters)
      .then((res) => {
        if (!cancelled) {
          setToday(res.today);
          setTomorrow(res.tomorrow);
          setOverdue(res.overdue);
          setCounts(res.counts);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || t("analytics.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters.operatorId, filters.task, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }
  if (error) {
    return <ErrorMessage message={error} onRetry={() => setError(null)} />;
  }

  const renderList = (title: string, items: AnalyticsFollowUpItem[]) => (
    <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
      <h3 className="px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-200">{title}</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("common.name")}</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("common.phone")}</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("common.city")}</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("analytics.task")}</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Follow-up date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{t("common.notes")}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-4 text-sm text-gray-500 text-center">
                  {t("analytics.noData")}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.leadId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{item.name}</td>
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{item.phone}</td>
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{item.city ?? "—"}</td>
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{item.task ?? "—"}</td>
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{formatDate(item.followUpDate)}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap max-w-xs truncate">{item.followUpNotes ?? "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">{t("analytics.followupsToday")}</p>
          <p className="text-2xl font-semibold text-gray-900">{counts.today}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">{t("analytics.followupsTomorrow")}</p>
          <p className="text-2xl font-semibold text-gray-900">{counts.tomorrow}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">{t("analytics.followupsOverdue")}</p>
          <p className="text-2xl font-semibold text-gray-900">{counts.overdue}</p>
        </div>
      </div>
      {renderList(`${t("analytics.followupsToday")} (${counts.today})`, today)}
      {renderList(`${t("analytics.followupsTomorrow")} (${counts.tomorrow})`, tomorrow)}
      {renderList(`${t("analytics.followupsOverdue")} (${counts.overdue})`, overdue)}
    </div>
  );
};

export default FollowupsTab;
